import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Transition } from '@headlessui/react';
import { AnimatedPresence } from '../ui/AnimatedPresence';
import { IconChevronUp, IconDotsVertical, IconEye, IconEyeOff } from '@tabler/icons-react';
import { CloseIcon, InfoIcon, WarningIcon, MessageIcon, EndCallIcon, ScreenShareIcon, CcIcon } from '@/components/icons';
import type { ConversationTurn, ResearchEvent, InterviewContext } from '../../types';

import { MicIcon } from '../icons/MicIcon';
import { MicOffIcon } from '../icons/MicOffIcon';
import { VideoIcon } from '../icons/VideoIcon';
import { VideoOffIcon } from '../icons/VideoOffIcon';
import { CheckIcon } from '../icons/CheckIcon';

import Spinner from '../Spinner';
import { ButtonContent } from '../ui/ButtonContent';
import GlowingOrb from '../GlowingOrb';

import { EndInterviewModal } from './modals/EndInterviewModal';
import { EndingCountdownModal } from './modals/EndingCountdownModal';
import { InactivityCountdownModal } from './modals/InactivityCountdownModal';

import { ReportIssueModal } from './modals/ReportIssueModal';

import { useCountdowns } from './hooks/useCountdowns';
import { useAudioPipeline } from './hooks/useAudioPipeline';
import { useSessionWebSocket, ConnectionState } from './hooks/useSessionWebSocket';
import { useScreenShare } from './hooks/useScreenShare';
import { useAuthorizedConversationViewer } from './hooks/useAuthorizedConversationViewer';
import { ScreenSharePromptModal } from './modals/ScreenSharePromptModal';
import { InformationBlockMount } from './InformationBlockMount';
import { SpokenInformationTile } from './SpokenInformationTile';
import { ScreenShareService } from '../../services/screenShareService';
import { captureEvent } from '../../lib/telemetry/posthog/frontend';
import { buildLiveInterviewBaseProps } from '../../lib/telemetry/live-interview-contract';
import { getAudioContextConstructor } from '../../lib/utils/audio-context';
import { resolveConnectionErrorPresentation } from './connection-error-presentation';
import type { RespondentConversationState } from '../../lib/multimodal-contract';

/**
 * Thin orchestrator for the real-time AI interview interface.
 * Creates shared refs, composes hooks, and renders the call UI.
 * All business logic lives in the hooks; this component only wires them together.
 */
interface CallScreenProps {
  onEndCall: (conversation: ConversationTurn[], researchEvents: ResearchEvent[]) => void;
  onPerformResearch: (query: string, type: 'primary' | 'background', existingSummary?: string) => Promise<string>;
  researchEvents: ResearchEvent[];
  context: InterviewContext;
  preferredDeviceId?: string;
  preferredSpeakerId?: string;
  preferredCameraId?: string;
  videoEnabled?: boolean;
  /** Whether the user toggled camera on in the lobby (defaults to true) */
  userCameraEnabled?: boolean;
  /** When true, camera is mandatory - hides the in-call camera toggle */
  requireVideo?: boolean;
  roomId: string;
  passcode: string;
  /** Interview data ID for multi-response tracking */
  interviewDataId?: string;
  /** Enforced interview language code from campaign settings */
  enforceLanguage?: string | null;
  /** Whether the campaign allows AI- or user-initiated screen sharing (requires videoEnabled). */
  screenShareEnabled?: boolean;
  /** Whether the meeting-info slide-over panel is open. */
  infoPanelOpen?: boolean;
  /** Called when the panel should open/close. */
  onInfoPanelChange?: (open: boolean) => void;
  /** The shared thread stays mounted while the panel is closed. */
  conversationPanel?: React.ReactNode;
  conversationPanelOpen?: boolean;
  onConversationPanelChange?: (open: boolean) => void;
  onConversationState?: (state: RespondentConversationState) => void;
  /** Authoritative active content can be shared without revealing the conversation panel. */
  conversationState?: RespondentConversationState | null;
  onInformationPresented?: (fieldId: string) => Promise<boolean>;
  /** Same-session grant; presence enables media-only exit behavior. */
  responseToken?: string;
}

/**
 * Animated 3-bar level meter driven by a Web Audio `AnalyserNode` on the mic input.
 * Bars are symmetric: middle bar responds strongest, outer bars scale down -
 * the meter grows from the centre outward rather than sweeping left-to-right.
 */
const MicLevelBars: React.FC<{ analyser: AnalyserNode | null }> = ({ analyser }) => {
  const bar1Ref = useRef<HTMLSpanElement>(null);
  const bar2Ref = useRef<HTMLSpanElement>(null);
  const bar3Ref = useRef<HTMLSpanElement>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!analyser) {
      if (bar1Ref.current) bar1Ref.current.style.transform = 'scaleY(0.3)';
      if (bar2Ref.current) bar2Ref.current.style.transform = 'scaleY(0.5)';
      if (bar3Ref.current) bar3Ref.current.style.transform = 'scaleY(0.3)';
      return undefined;
    }
    const bufferLen = analyser.frequencyBinCount;
    const data = new Uint8Array(bufferLen);
    const tick = () => {
      analyser.getByteFrequencyData(data);
      // Single overall level (0..1). Divisor tuned so normal speech pushes the bars near full height,
      // while quiet moments sink toward the baseline - gives the meter visible movement.
      let sum = 0;
      for (let i = 0; i < bufferLen; i++) sum += data[i];
      const level = Math.min(1, (sum / bufferLen) / 80);
      const center = 0.15 + level * 0.85;   // middle bar - strongest response
      const outer = 0.15 + level * 0.65;    // outer bars - grows from the centre outward
      if (bar1Ref.current) bar1Ref.current.style.transform = `scaleY(${outer.toFixed(2)})`;
      if (bar2Ref.current) bar2Ref.current.style.transform = `scaleY(${center.toFixed(2)})`;
      if (bar3Ref.current) bar3Ref.current.style.transform = `scaleY(${outer.toFixed(2)})`;
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [analyser]);

  const barClass = 'w-[2px] h-4 rounded-full bg-foreground origin-center transition-transform duration-75';
  return (
    <span className="flex items-center gap-[3px]" aria-hidden="true">
      <span ref={bar1Ref} className={barClass} style={{ transform: 'scaleY(0.3)' }} />
      <span ref={bar2Ref} className={barClass} style={{ transform: 'scaleY(0.5)' }} />
      <span ref={bar3Ref} className={barClass} style={{ transform: 'scaleY(0.3)' }} />
    </span>
  );
};

/** Shared pill-button classes for the Meet-style bottom bar (rounded rectangles). */
const pillBase = 'relative flex items-center justify-center h-[52px] rounded-full border transition-all';
const pillStd = 'px-4 md:px-[22px]';   // Standard width for single-icon pills (mic, cam, share, CC, hand)
const pillSlim = 'px-3 md:px-[14px]';  // Thinner - e.g. three-dot menu
const pillIdle = 'bg-surface hover:bg-surface-hover border-border text-foreground hover:text-foreground';
const pillActive = 'bg-danger/20 hover:bg-danger/30 border-danger/60 text-danger';
const pillDisabled = 'bg-surface/50 border-border text-muted-foreground cursor-not-allowed';

/**
 * Size classes for the mounted information-block tile, responsive to how it
 * stacks against hollie's orb:
 *  - Portrait phone (default, flex-col): the tile drops in ABOVE the orb, so it
 *    claims a slice of the cell HEIGHT and spans the full width. A tall portrait
 *    image takes more height; a wide landscape one stays short.
 *  - Desktop (md, flex-row): the tile slides in to the LEFT of the orb, so it
 *    claims a slice of the cell WIDTH and spans the full height. A wide landscape
 *    image gets a wider tile that compresses the orb; a portrait one stays narrow.
 * Literal classes so Tailwind emits them.
 */
function infoBlockTileSizeClass(aspect: number | null): string {
  // Mobile height fraction (vertical axis) and desktop width fraction (horizontal axis).
  let heightClass = 'h-[50%]';
  let widthClass = 'md:w-[50%]';
  if (aspect !== null && aspect >= 1.6) {
    heightClass = 'h-[40%]';
    widthClass = 'md:w-[62%]';
  } else if (aspect !== null && aspect < 0.9) {
    heightClass = 'h-[58%]';
    widthClass = 'md:w-[36%]';
  }
  return `w-full ${heightClass} md:h-full ${widthClass}`;
}

const CallScreen: React.FC<CallScreenProps> = ({ onEndCall, onPerformResearch, researchEvents, context, preferredDeviceId, preferredSpeakerId, preferredCameraId, videoEnabled = false, userCameraEnabled = true, requireVideo = false, roomId, passcode, interviewDataId, enforceLanguage, screenShareEnabled = false, infoPanelOpen = false, onInfoPanelChange, conversationPanel, conversationPanelOpen = false, onConversationPanelChange, onConversationState, conversationState, onInformationPresented, responseToken }) => {
  // Shared refs owned by orchestrator, passed to all hooks
  const wsRef = useRef<WebSocket | null>(null);
  const wsReadyRef = useRef<boolean>(false);
  const connectionErrorRef = useRef<HTMLDivElement>(null);

  const [showCaptions, setShowCaptions] = useState(false);
  const [showMicDevices, setShowMicDevices] = useState(false);
  // Screen-share error toast state. We track which error message has been
  // dismissed so a new failure re-surfaces the toast rather than staying
  // hidden forever. Main use-case: macOS users who have not granted the
  // browser "Screen & System Audio Recording" permission in TCC - the
  // share attempt silently dies otherwise.
  const [dismissedScreenShareError, setDismissedScreenShareError] = useState<string | null>(null);
  // Side panels are controlled by the parent so call and text modes share state.
  const showInfo = infoPanelOpen && !conversationPanelOpen;
  const setShowInfo = useCallback((v: boolean) => {
    if (v) onConversationPanelChange?.(false);
    onInfoPanelChange?.(v);
  }, [onInfoPanelChange, onConversationPanelChange]);
  const [currentTime, setCurrentTime] = useState(() => new Date());
  // Latches `true` the first time the AI speaks - used so the GlowingOrb stays
  // in its `connecting` state across the entire pre-interview bring-up (WS
  // connect, mic grant, context send) until the AI actually greets. Without
  // this the orb would flicker to `listening` the moment status reads
  // "Listening...", even though the AI hasn't said anything yet.
  const [hasAiSpoken, setHasAiSpoken] = useState(false);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const micDevicesRef = useRef<HTMLDivElement>(null);
  const micDevicesTriggerRef = useRef<HTMLButtonElement>(null);
  const reportMenuTriggerRef = useRef<HTMLButtonElement>(null);

  const countdowns = useCountdowns({ wsRef });
  const audioPipeline = useAudioPipeline({ wsRef, wsReadyRef, preferredDeviceId, preferredSpeakerId });
  const trackInCallControl = useCallback(
    (eventName: string, properties?: Record<string, string>) => {
      captureEvent(eventName, {
        ...buildLiveInterviewBaseProps(roomId, 'in_call', interviewDataId),
        ...(properties || {}),
      });
    },
    [roomId, interviewDataId],
  );
  // Screen share requires (a) the campaign DB flag, (b) video enabled
  // (we reuse the recording pipeline), and (c) a platform that can actually
  // acquire a display-media stream. iOS/Android physically cannot - see
  // ScreenShareService.isSupported. Worker mirrors this veto via
  // clientSupportsScreenShare so the provider never receives the tool either.
  const screenShareAvailable = screenShareEnabled && videoEnabled && ScreenShareService.isSupported();
  // Pass a getter (not the stream itself) so we always pick up the current
  // mixed-audio destination at share-start time - the pipeline can swap
  // devices mid-call and reassign this ref.
  //
  // We clone a track from `recordingAudioDestRef.current.stream` (not from
  // the raw mic), because that destination carries the SAME mic + AI-playback
  // mix that the camera recording uses. This keeps screen-share audio in
  // parity with the camera recording - admins hear both sides of the
  // conversation when reviewing a screen share. The clone is independent:
  // stopping it on share teardown won't disturb the camera recording or the
  // AI input path.
  const screenShare = useScreenShare({
    wsRef,
    wsReadyRef,
    flowId: roomId,
    interviewDataId,
    screenShareEnabled: screenShareAvailable,
    getAudioStream: useCallback(
      () => audioPipeline.recordingAudioDestRef.current?.stream ?? null,
      [audioPipeline.recordingAudioDestRef],
    ),
  });
  // A closing dialog keeps its final content while the shared modal animates out.
  const closingModalContent = useRef({ ending: 0, inactivity: 0, inactivityTotal: 1, shareReason: '' });
  if (countdowns.endingCountdown !== null) closingModalContent.current.ending = countdowns.endingCountdown;
  if (countdowns.inactivityCountdown !== null) {
    closingModalContent.current.inactivity = countdowns.inactivityCountdown;
    closingModalContent.current.inactivityTotal = countdowns.inactivityTotalSeconds || 1;
  }
  if (screenShare.pendingRequest) closingModalContent.current.shareReason = screenShare.pendingRequest.reason;
  const session = useSessionWebSocket({
    wsRef,
    wsReadyRef,
    audioPipeline,
    countdowns,
    screenShare,
    props: { onEndCall, onPerformResearch, researchEvents, context, videoEnabled, userCameraEnabled, preferredCameraId, roomId, passcode, interviewDataId, enforceLanguage, responseToken, onConversationState },
  });
  const {
    authorizedConversationId,
    authorizedUnavailableReason,
  } = useAuthorizedConversationViewer(
    roomId,
    interviewDataId,
    session.hasConnectionError
      && session.connectionErrorCode === 'conversation_unavailable',
  );

  const connectionErrorPresentation = resolveConnectionErrorPresentation({
    code: session.connectionErrorCode,
    fallbackMessage: session.connectionErrorMessage,
    retryable: session.connectionErrorRetryable,
    authorizedConversationId,
    authorizedUnavailableReason,
  });
  const hasConnectionErrorActions = !!responseToken || connectionErrorPresentation.retryable
    || connectionErrorPresentation.links.length > 0;

  useEffect(() => {
    if (session.hasConnectionError) {
      connectionErrorRef.current?.focus();
    }
  }, [session.hasConnectionError]);

  // One-way latch: once the AI speaks its first greeting, `hasAiSpoken` stays true
  // for the rest of the call so the orb never rewinds to `connecting` after the
  // intro. Mid-call reconnects still route through `connecting` via session.status.
  useEffect(() => {
    if (audioPipeline.isAiSpeaking && !hasAiSpoken) setHasAiSpoken(true);
  }, [audioPipeline.isAiSpeaking, hasAiSpoken]);

  const isBackgroundResearchComplete = !researchEvents.some(e => e.type === 'background' && e.status !== 'complete');
  const userDisplayName = context.intervieweeName || 'You';
  const intervieweeLabel = context.intervieweeName || 'Guest';
  const timeString = currentTime.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  // Aspect ratio of the mounted information-block image, so its tile can size
  // itself (wider for a landscape image, narrower for a portrait one). Reset
  // whenever the mounted block changes so a new image measures fresh.
  const [infoBlockAspect, setInfoBlockAspect] = useState<number | null>(null);
  const activeField = conversationState?.activeField;
  const sharedInformation = activeField?.type === 'information' && activeField.answerChannel !== 'on_screen'
    && activeField.information?.fieldId === activeField.fieldId && activeField.information.passive !== false
    && activeField.information.delivery !== 'on_screen' ? activeField.information : null;
  const mountedInfoBlockField = sharedInformation?.fieldId ?? session.mountedInformationBlock?.fieldName;
  useEffect(() => {
    setInfoBlockAspect(null);
  }, [mountedInfoBlockField]);

  // While hollie shares her own screen (an information-block image mounts beside
  // her orb) the stage is crowded with self-view + shared media + orb all
  // competing for room. Auto-collapse the self-view so the shared media and the
  // orb get the space. This is a derived, temporary hide layered over the user's
  // manual preference: it is never written back to `isVideoPreviewMinimized`
  // (which persists to localStorage), so the instant hollie stops sharing the
  // self-view returns to whatever the user had chosen.
  const isSharingMedia = !!sharedInformation || !!session.mountedInformationBlock;
  const selfViewMinimized = session.isVideoPreviewMinimized || isSharingMedia;

  // Auto-scroll transcript to the latest turn whenever the conversation updates.
  useEffect(() => {
    const el = transcriptRef.current;
    if (!el) return;
    // Prefer smooth scrollTo; fall back to scrollTop for environments (e.g. jsdom) that lack it.
    if (typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    } else {
      el.scrollTop = el.scrollHeight;
    }
  }, [session.conversation]);

  // Tick the clock every 30s for the bottom-bar time display
  useEffect(() => {
    const id = setInterval(() => setCurrentTime(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);

  // Close mic device popover on outside click
  useEffect(() => {
    if (!showMicDevices) return undefined;
    const handler = (e: MouseEvent) => {
      if (micDevicesRef.current && !micDevicesRef.current.contains(e.target as Node)) {
        setShowMicDevices(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.isComposing) return;
      event.preventDefault();
      setShowMicDevices(false);
      micDevicesTriggerRef.current?.focus();
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showMicDevices]);

  useEffect(() => {
    if (!session.showReportMenu) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.isComposing) return;
      event.preventDefault();
      session.setShowReportMenu(false);
      reportMenuTriggerRef.current?.focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [session.showReportMenu, session.setShowReportMenu]);

  /**
   * Short "power-up" chime on mount - signals the user has joined.
   *
   * Isolation from hollie and the realtime provider:
   * 1. The chime runs in its own `AudioContext` that only connects to `ctx.destination` (local speakers).
   *    It never touches `useAudioPipeline`'s input graph, so it cannot be routed into the WebSocket stream.
   * 2. This effect fires on mount, before `session.startSession()` runs `setupAudioPipeline()`. The mic
   *    capture graph (ScriptProcessor → WS) doesn't exist yet, so there's nothing to leak into.
   * 3. `aiIntroStartedRef` in the audio pipeline also gates all upstream mic sends until the AI's first
   *    audio chunk lands - a second-line defence even if timing ever changes.
   *
   * Guard: if the mic pipeline is somehow already active (future refactor), skip the chime so we never
   * risk acoustic feedback being captured.
   */
  useEffect(() => {
    if (audioPipeline.analyser !== null) return undefined; // Pipeline already up - don't risk feedback.
    const AudioCtx = getAudioContextConstructor();
    if (!AudioCtx) return undefined;
    let ctx: AudioContext;
    try {
      ctx = new AudioCtx();
    } catch {
      return undefined; // User agent refused to create a context (rare)
    }

    const now = ctx.currentTime;

    // Two-step swell: a warm mid-range chord lands first, then a brighter resolution floats in on
    // top - feels upbeat and positive without losing the ambient enterprise polish. Gentle low-pass
    // keeps the edges soft; filter cutoff opened up vs the earlier pass so it doesn't feel bass-heavy.
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 3200;
    filter.Q.value = 0.7;
    filter.connect(ctx.destination);

    /** Play a single sine voice with a gentle swell into the shared low-pass bus. */
    const voice = (freq: number, peak: number, offset: number, attack: number, decay: number) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const start = now + offset;
      const end = start + attack + decay;
      g.gain.setValueAtTime(0, start);
      g.gain.linearRampToValueAtTime(peak, start + attack);
      g.gain.exponentialRampToValueAtTime(0.0005, end);
      osc.connect(g);
      g.connect(filter);
      osc.start(start);
      osc.stop(end + 0.05);
    };

    // Step 1 - warm C-major foundation in mid register (not sub-bass): C4 + E4 + G4.
    voice(261.63, 0.14, 0.00, 0.06, 0.85); // C4 - root
    voice(329.63, 0.10, 0.01, 0.06, 0.85); // E4 - major third (the "positive" colour)
    voice(392.0,  0.10, 0.02, 0.06, 0.85); // G4 - fifth

    // Step 2 - brighter resolution an octave up enters ~250 ms later: C5 + E5.
    voice(523.25, 0.09, 0.25, 0.05, 0.70); // C5
    voice(659.25, 0.08, 0.27, 0.05, 0.70); // E5 shimmer

    // Tear the context down once the chime has finished so we don't leave an audio graph open.
    const cleanupId = window.setTimeout(() => { ctx.close().catch(() => {}); }, 1400);
    return () => {
      clearTimeout(cleanupId);
      if (ctx.state !== 'closed') ctx.close().catch(() => {});
    };
  }, []);

  // Bridges audio toggle with session status (audio hook doesn't own status text)
  const handleTogglePause = useCallback(() => {
    audioPipeline.togglePause();
    // isPaused reflects the pre-toggle value here (React hasn't batched the update yet)
    if (audioPipeline.isPaused) {
      session.setStatus("Listening...");
      trackInCallControl('live_interview.control_mic_toggled', { state: 'on' });
    } else {
      session.setStatus("Muted");
      trackInCallControl('live_interview.control_mic_toggled', { state: 'off' });
    }
  }, [audioPipeline.togglePause, audioPipeline.isPaused, session.setStatus, trackInCallControl]);

  // Hide/show the self-view CARD only. Research shows respondents fixate on
  // their own image; collapsing the tile removes that distraction. This is
  // deliberately NOT a camera toggle - the stream stays live and the recording
  // keeps running, so the agent and the recording still see them.
  const handleToggleSelfView = useCallback((minimized: boolean) => {
    session.setIsVideoPreviewMinimized(minimized);
    trackInCallControl('live_interview.control_self_view_toggled', {
      state: minimized ? 'hidden' : 'shown',
    });
  }, [session.setIsVideoPreviewMinimized, trackInCallControl]);

  useEffect(() => {
    if (!isBackgroundResearchComplete) {
      session.setStatus("Gathering background intelligence...");
      return undefined;
    }

    const abortController = new AbortController();
    void session.startSession(abortController.signal);

    return () => {
      abortController.abort();
      session.cleanupResources();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBackgroundResearchComplete, session.startSession, session.cleanupResources]);

  return (
    <div className="flex flex-col h-full bg-background relative">

      {/* Full-page connection failure and its verified recovery controls. */}
      {session.hasConnectionError && (
        <div className="absolute inset-0 flex items-center justify-center p-4 bg-background z-50">
          <div
            ref={connectionErrorRef}
            role="alert"
            aria-labelledby="connection-error-title"
            tabIndex={-1}
            className="max-w-md w-full bg-background border border-border rounded-2xl p-8 text-center"
          >
            <div className="w-16 h-16 bg-wash-danger rounded-full flex items-center justify-center mx-auto mb-6">
              <WarningIcon className="w-8 h-8 text-danger-soft" />
            </div>

            <h1 id="connection-error-title" className="text-2xl font-bold text-foreground mb-4">
              {connectionErrorPresentation.title}
            </h1>
            {connectionErrorPresentation.message && (
              <p className={`text-muted-foreground ${hasConnectionErrorActions ? 'mb-8' : ''}`}>
                {connectionErrorPresentation.message}
              </p>
            )}

            {hasConnectionErrorActions && (
              <div className="space-y-3">
                {responseToken ? (
                  <button type="button" onClick={session.handleConfirmEnd}
                    className="w-full py-3 button-accent rounded-lg cursor-pointer transition-colors">
                    {requireVideo ? 'Return to setup' : 'Continue in text'}
                  </button>
                ) : connectionErrorPresentation.retryable && (
                  <button
                    onClick={() => window.location.reload()}
                    className="w-full py-3 button-accent font-bold rounded-lg transition-colors"
                  >
                    Retry Connection
                  </button>
                )}
                {connectionErrorPresentation.links.map((link) => (
                  <a
                    key={`${link.to}:${link.label}`}
                    href={link.to}
                    className={`block w-full py-3 font-bold rounded-lg transition-colors ${
                      link.emphasis === 'primary'
                        ? 'button-accent'
                        : 'bg-surface-raised text-foreground hover:bg-surface-hover'
                    }`}
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* End Interview Confirmation Modal */}
      <EndInterviewModal
        open={session.showEndModal}
        onConfirm={session.handleConfirmEnd}
        onCancel={session.handleCancelEnd}
        questionsComplete={session.questionsComplete}
        continuesInText={!!responseToken}
        requiresVideoToContinue={requireVideo}
      />

      {/* Ending Countdown Modal (AI is saying goodbye) */}
      <EndingCountdownModal
        open={countdowns.endingCountdown !== null}
        countdown={closingModalContent.current.ending}
        onCancel={countdowns.cancelEndingCountdown}
      />

      {/* Inactivity Warning Countdown Modal */}
      <InactivityCountdownModal
        open={countdowns.inactivityCountdown !== null}
        countdown={closingModalContent.current.inactivity}
        totalSeconds={closingModalContent.current.inactivityTotal}
        onCancel={countdowns.handleCancelInactivity}
      />

      {/* Screen share consent prompt (AI-initiated) */}
      <ScreenSharePromptModal
        open={Boolean(screenShare.pendingRequest)}
        reason={closingModalContent.current.shareReason}
        onAccept={screenShare.accept}
        onDecline={screenShare.decline}
      />

      {/* Report Issue Modal */}
      <ReportIssueModal
        open={session.showReportModal}
        reportIssueText={session.reportIssueText}
        onTextChange={(e) => session.setReportIssueText(e.target.value)}
        onSubmit={session.handleReportIssue}
        onCancel={() => session.setShowReportModal(false)}
      />

      {/* Main Center Stage + push-aside info panel */}
      <div className="grow flex flex-row min-h-0 relative">

        {/* Stage */}
        <div className={`grow flex flex-col relative min-h-0 min-w-0 ${showInfo || conversationPanelOpen ? 'hidden md:flex' : ''}`}>

        {!isBackgroundResearchComplete ? (
          <div className="grow flex flex-col items-center justify-center gap-4 animate-fade-in">
            <Spinner size="w-24 h-24" borderWidth="border-[6px]" />
            <div className="text-center">
              <p className="text-accent-soft font-bold text-lg animate-pulse">Researching {context.intervieweeCompany || context.intervieweeName || 'Subject'}...</p>
              <p className="text-subtle-foreground text-sm">Preparing context</p>
            </div>
          </div>
        ) : (
          <>
            {/* Tiles - fill full width/height of the stage. Flex (not grid) so
                the self-view can animate its flex-basis to 0 and hollie's cell,
                which carries `grow`, smoothly absorbs the freed space instead of
                the layout snapping. Stacks vertically on a portrait phone and
                side-by-side on desktop. */}
            <div className="grow min-h-0 p-2 md:p-3 flex flex-col md:flex-row">

              {/* User Tile - only when videoEnabled. Kept mounted while hidden
                  (collapsed to basis-0 + faded, NOT display:none) so the camera
                  stream stays attached to the preview and recording is never
                  interrupted - only the card animates out of view. Its trailing
                  margin doubles as the inter-tile gap and collapses with it so
                  hollie ends up flush to the stage edge. */}
              {videoEnabled && (
                <div
                  data-testid="self-view-tile"
                  className={`relative rounded-xl overflow-hidden bg-background border border-border min-h-0 shrink-0 transition-[width,height,opacity,margin] duration-300 ease-out motion-reduce:transition-none ${
                    selfViewMinimized
                      ? 'w-full h-0 md:h-full md:w-0 opacity-0 mb-0 md:mr-0 pointer-events-none'
                      : 'w-full h-1/2 md:h-full md:w-1/2 opacity-100 mb-2 md:mb-0 md:mr-3'
                  }`}
                  aria-hidden={selfViewMinimized}
                >
                  <video
                    ref={session.videoPreviewRef}
                    autoPlay
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                  />
                  {session.isVideoPaused && (
                    <div className="absolute inset-0 bg-background flex items-center justify-center">
                      <div className="text-center">
                        <VideoOffIcon className="w-12 h-12 text-subtle-foreground mx-auto mb-2" />
                        <span className="text-muted-foreground text-sm font-medium">Camera off</span>
                      </div>
                    </div>
                  )}
                  {/* Hide self-view - paired with the name chip along the bottom so
                      it never crowds the top edge. Removes the card from view; the
                      camera keeps running (this is not the camera toggle). Styled
                      as a platform control pill (surface + hairline border). */}
                  {!selfViewMinimized && (
                    <button
                      onClick={() => handleToggleSelfView(true)}
                      className="absolute bottom-3 right-3 z-10 p-2 rounded-full bg-surface hover:bg-surface-hover border border-border text-foreground transition-colors"
                      title="Hide your video"
                      aria-label="Hide your video"
                    >
                      <IconEyeOff className="w-[18px] h-[18px]" stroke={2} />
                    </button>
                  )}
                  {/* Name chip, lower-left */}
                  <div className="absolute bottom-3 left-3 text-white text-sm font-medium bg-black/40 backdrop-blur-sm px-2.5 py-1 rounded-lg">
                    {userDisplayName}
                  </div>
                </div>
              )}

              {/* hollie cell - an information-block image tile mounts beside her
                  orb, INSIDE the stage (not an overlay). The two tiles stack
                  top-to-bottom on a portrait phone (flex-col: the media drops in
                  from ABOVE) and sit side-by-side on desktop (flex-row: the media
                  slides in from the LEFT). The tile's size tracks the image aspect.
                  A flat dark field: hollie's own halo + breathing aura are the
                  only glow here (docs/DESIGN.md: glow belongs to her orb alone),
                  so nothing external competes with her. */}
              <div className="flex grow min-h-0 min-w-0 flex-col md:flex-row gap-2 md:gap-3">
                <div
                  className={`min-h-0 min-w-0 overflow-hidden transition-[width,height,opacity] duration-300 ease-out ${
                    isSharingMedia
                      ? `${infoBlockTileSizeClass(infoBlockAspect)} opacity-100`
                      : 'h-0 w-full md:h-full md:w-0 opacity-0'
                  }`}
                  aria-hidden={!isSharingMedia}
                >
                  {sharedInformation ? <SpokenInformationTile key={`${conversationState?.responseId}:${sharedInformation.fieldId}`} information={sharedInformation}
                    onPresented={onInformationPresented} onAspectRatio={setInfoBlockAspect} /> : <InformationBlockMount
                    block={session.mountedInformationBlock}
                    onAspectRatio={setInfoBlockAspect}
                  />}
                </div>

                <div className="relative grow rounded-xl overflow-hidden border border-border flex items-center justify-center min-h-0 bg-background" data-theme="dark">
                  <div className="relative z-10 w-[55%] max-w-[320px] min-w-[160px] aspect-square">
                    <GlowingOrb
                      state={
                        audioPipeline.isAiSpeaking
                          ? 'speaking'
                          : !hasAiSpoken
                          ? 'connecting'
                          : session.connectionState === ConnectionState.Connected
                          ? 'listening'
                          : 'connecting'
                      }
                      analyserNode={audioPipeline.analyser}
                      // Screen sharing: she watches what you show her, so she
                      // holds "eyes". Otherwise she choreographs her own form from
                      // state (connecting ripples, speaking gathers whole,
                      // listening roams calm to playful).
                      form={screenShare.isSharing ? 'eyes' : undefined}
                    />
                  </div>
                  <div className="absolute z-10 bottom-3 left-3 text-white text-sm font-medium bg-black/40 backdrop-blur-sm px-2.5 py-1 rounded-lg">
                    hollie
                  </div>
                </div>
              </div>
            </div>

            {/* Show self-view - floating affordance shown only while the card is
                hidden by the user's own choice, so the respondent can bring their
                preview back. Suppressed while hollie shares media (isSharingMedia):
                the auto-hide freed that space for her shared tile, and re-revealing
                the self-view would defeat it. Camera was never stopped, so this just
                re-reveals the tile. Same control-pill language as the bottom bar.
                Insets are per-breakpoint: on mobile the tile collapses ABOVE hollie
                so the card sits a little lower (extra top inset balances it); on
                desktop it collapses to the side and the card is corner-aligned, so
                equal insets read evenly. */}
            {videoEnabled && session.isVideoPreviewMinimized && !isSharingMedia && (
              <button
                onClick={() => handleToggleSelfView(false)}
                className="absolute top-8 md:top-6 right-6 z-20 flex items-center gap-2 h-9 px-3 rounded-full bg-surface hover:bg-surface-hover border border-border text-foreground hover:text-foreground text-sm font-medium transition-colors animate-fade-in"
                title="Show your video"
                aria-label="Show your video"
              >
                <IconEye className="w-[18px] h-[18px]" stroke={2} />
                <span className="hidden md:inline">Show your video</span>
              </button>
            )}

            {/* Captions - closed-caption style: transparent, content centered. Always rendered so we can animate the height. */}
            <div
              className={`shrink-0 w-full overflow-hidden transition-[max-height] duration-300 ease-out ${showCaptions ? 'max-h-[200px] md:max-h-[220px]' : 'max-h-0'}`}
              aria-hidden={!showCaptions}
            >
              <div
                ref={transcriptRef}
                className="w-full h-[200px] md:h-[220px] overflow-y-auto scrollbar-dark py-3"
              >
                <div className="mx-auto max-w-4xl px-4 md:px-8 space-y-4">
                  {session.conversation.map((turn, idx) => {
                    const isAi = turn.speaker === 'ai';
                    const speakerName = isAi ? 'hollie' : intervieweeLabel;
                    const initial = isAi ? 'h' : (userDisplayName.charAt(0) || 'Y').toUpperCase();
                    return (
                      <div key={idx} className="flex gap-3 items-start">
                        <div
                          className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold ${isAi ? 'bg-volt/25 text-accent-soft' : 'bg-surface-raised text-foreground'}`}
                          aria-hidden="true"
                        >
                          {initial}
                        </div>
                        <div className="min-w-0 pt-0.5">
                          <p className="text-muted-foreground text-xs font-semibold mb-0.5">{speakerName}</p>
                          <p className="text-foreground text-lg md:text-xl leading-snug">
                            {turn.text}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </>
        )}
        </div>

        {/* Info panel - inline, pushes the stage aside with a smooth width slide */}
        <aside
          className={`shrink-0 min-h-0 overflow-hidden transition-[width] duration-300 ease-out motion-reduce:transition-none ${showInfo ? 'w-full md:w-[336px] lg:w-[400px]' : 'w-0'}`}
          aria-hidden={!showInfo}
          inert={!showInfo}
          aria-label="Meeting details"
        >
          <div className="w-full md:w-[336px] lg:w-[400px] h-full p-0 md:p-3 md:pl-0">
            <div className="h-full bg-background md:border md:border-border md:rounded-xl flex flex-col overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <h2 className="text-foreground font-semibold text-base">Meeting details</h2>
                <button
                  onClick={() => {
                    captureEvent('live_interview.control_info_panel_toggled', {
                      ...buildLiveInterviewBaseProps(roomId, 'in_call', interviewDataId),
                      state: 'closed',
                    });
                    setShowInfo(false);
                  }}
                  className="p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-surface-hover transition-colors"
                  title="Close"
                  aria-label="Close meeting details"
                >
                  <CloseIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto scrollbar-dark px-5 py-5 space-y-5">
                <section>
                  <h3 className="text-foreground font-medium text-sm mb-3">Joining info</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <p className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Room code</p>
                      <p className="font-mono text-foreground break-all">{roomId}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Host</p>
                      <p className="text-foreground">hollie · your AI host</p>
                    </div>
                  </div>
                </section>

                <section className="pt-5 border-t border-border">
                  <p className="text-muted-foreground text-xs uppercase tracking-wider mb-2">Status</p>
                  <p className={`text-sm ${session.status.startsWith('Error') ? 'text-danger' : 'text-foreground'}`}>
                    {session.status}
                  </p>
                </section>

                <section className="pt-5 border-t border-border">
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    This session is recorded and may be shared with third parties. You can end the call any time using the red button.
                  </p>
                </section>
              </div>
            </div>
          </div>
        </aside>
        {conversationPanel ? (
          <aside aria-label="Conversation panel" aria-hidden={!conversationPanelOpen} inert={!conversationPanelOpen}
            className={`shrink-0 min-h-0 overflow-hidden transition-[width] duration-300 ease-out motion-reduce:transition-none ${conversationPanelOpen ? 'w-full md:w-[336px] lg:w-[400px]' : 'w-0'}`}>
            <div className="w-full md:w-[336px] lg:w-[400px] h-full p-0 md:p-3 md:pl-0">
              <div className="h-full bg-background md:border md:border-border md:rounded-xl flex flex-col overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                  <h2 className="text-foreground font-semibold text-base">Conversation</h2>
                  <button type="button" aria-label="Close conversation" className="p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-surface-hover transition-colors"
                    onClick={() => onConversationPanelChange?.(false)}><CloseIcon className="w-5 h-5" /></button>
                </div>
                <div className="flex-1 min-h-0">{conversationPanel}</div>
              </div>
            </div>
          </aside>
        ) : null}
      </div>

      {/* Bottom Bar - Google Meet 3-column layout.
          pb keeps the prior responsive floor (16px mobile / 24px desktop) and
          adds the home-indicator safe-area inset on top via max(), so notched
          phones clear the indicator without shrinking desktop spacing. */}
      <div className="shrink-0 px-4 md:px-6 pt-2 pb-[max(env(safe-area-inset-bottom),1rem)] md:pb-[max(env(safe-area-inset-bottom),1.5rem)] z-20 touch-manipulation">

        {/* Screen-share error toast. Surfaces getDisplayMedia failures
            (most commonly macOS TCC "Screen Recording" not granted to the
            browser) that would otherwise leave the user staring at a dead
            share button. Dismissible - a new distinct error re-opens it. */}
        <AnimatedPresence show={Boolean(screenShare.lastError && screenShare.lastError !== dismissedScreenShareError)}>
          <div className="flex justify-center pb-3">
            <div className="flex items-start gap-2 max-w-md bg-wash-warning text-foreground text-xs font-medium px-3 py-2 rounded-lg">
              <WarningIcon className="w-4 h-4 shrink-0 mt-0.5" />
              <span className="leading-snug">
                Couldn't start screen share: {screenShare.lastError}. On macOS, check
                System Settings → Privacy &amp; Security → Screen &amp; System Audio Recording.
              </span>
              <button
                onClick={() => setDismissedScreenShareError(screenShare.lastError)}
                className="ml-1 shrink-0 hover:text-foreground/80 transition-colors"
                aria-label="Dismiss"
              >
                <CloseIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </AnimatedPresence>

        {/* Optional screen-share banner (above control row) */}
        <AnimatedPresence show={screenShare.isSharing}>
          <div className="flex justify-center pb-3">
            <div className="flex items-center gap-2 bg-wash-danger text-foreground text-xs font-semibold px-3 py-1.5 rounded-full">
              <span className="w-2 h-2 rounded-full bg-danger animate-pulse" />
              Sharing your screen
              <button
                onClick={() => {
                  trackInCallControl('live_interview.control_screen_share_clicked', { state: 'stop' });
                  screenShare.stop('user_stopped');
                }}
                className="ml-2 underline hover:no-underline"
              >
                Stop
              </button>
            </div>
          </div>
        </AnimatedPresence>

        <div className="flex flex-col md:flex-row items-center justify-center md:justify-between gap-3">

          {/* LEFT: Time + Room code + status */}
          <div className="flex-1 min-w-0 hidden md:flex items-center gap-2 text-muted-foreground text-[15px]">
            <span className="font-medium text-muted-foreground">{timeString}</span>
            <span className="text-subtle-foreground">·</span>
            <span className="font-mono text-muted-foreground truncate" title={roomId}>{roomId}</span>
          </div>

          {/* CENTER: Control pills. flex-wrap is the narrow-screen safety net:
              the enlarged 52px pills can exceed a 320-360px viewport, and the
              app shell is overflow-hidden, so without wrapping the mic and
              End-call pills would clip. On phones wide enough they stay one row. */}
          <div className="flex flex-wrap items-center justify-center gap-1.5 md:gap-2">

            {/* Mic split pill - caret opens device picker, right half toggles mute */}
            <div
              ref={micDevicesRef}
              className={`relative flex items-center h-[52px] rounded-full border transition-all ${!isBackgroundResearchComplete ? pillDisabled : audioPipeline.isPaused ? pillActive : pillIdle}`}
            >
              <button
                ref={micDevicesTriggerRef}
                onClick={() => setShowMicDevices(v => !v)}
                disabled={!isBackgroundResearchComplete || audioPipeline.devices.length === 0}
                className="group h-full pl-4 md:pl-[22px] pr-2.5 md:pr-3 flex items-center rounded-l-full hover:bg-foreground/5 disabled:cursor-not-allowed disabled:opacity-60 transition-colors"
                title="Select microphone"
                aria-haspopup="menu"
                aria-expanded={showMicDevices}
              >
                {/* Level meter - default view when live; hidden on hover, when the popover is open, or when muted. */}
                {!audioPipeline.isPaused && (
                  <span className={`${showMicDevices ? 'hidden' : 'flex group-hover:hidden'} items-center`}>
                    <MicLevelBars analyser={audioPipeline.inputAnalyser} />
                  </span>
                )}
                {/* Caret - shown on hover, when the popover is open, or when muted. */}
                <IconChevronUp className={`${(showMicDevices || audioPipeline.isPaused) ? 'inline' : 'hidden group-hover:inline'} w-[13px] h-[13px] md:w-[15px] md:h-[15px] transition-transform ${showMicDevices ? 'rotate-180' : ''}`} stroke={2} />
              </button>
              <div className="w-px h-[22px] bg-current opacity-25" aria-hidden="true" />
              <button
                onClick={handleTogglePause}
                disabled={!isBackgroundResearchComplete}
                className="h-full pl-2.5 md:pl-3 pr-4 md:pr-[22px] flex items-center rounded-r-full hover:bg-foreground/5 disabled:cursor-not-allowed transition-colors"
                title={audioPipeline.isPaused ? 'Unmute microphone' : 'Mute microphone'}
              >
                {audioPipeline.isPaused ? <MicOffIcon className="w-[22px] h-[22px]" /> : <MicIcon className="w-[22px] h-[22px]" />}
              </button>

              {/* Device picker popover */}
              <Transition show={showMicDevices && audioPipeline.devices.length > 0} as={React.Fragment}>
                <div aria-hidden={!showMicDevices} inert={!showMicDevices} className="absolute bottom-full left-0 mb-3 w-72 origin-bottom-left bg-surface-raised border border-border rounded-xl overflow-hidden z-50 transition duration-200 data-closed:opacity-0 data-closed:translate-y-1 data-closed:scale-[0.98] data-leave:duration-150 motion-reduce:transition-none">
                  <p className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border/60">Microphone</p>
                  <div className="max-h-64 overflow-y-auto scrollbar-dark py-1">
                    {audioPipeline.devices.map(d => {
                      const isSelected = d.deviceId === audioPipeline.selectedDeviceId;
                      return (
                        <button
                          key={d.deviceId}
                          onClick={() => {
                            void audioPipeline.handleDeviceChange({ target: { value: d.deviceId } } as React.ChangeEvent<HTMLSelectElement>);
                            setShowMicDevices(false);
                          }}
                          className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 transition-colors ${isSelected ? 'text-accent-soft bg-surface-hover' : 'text-foreground hover:bg-surface-hover'}`}
                        >
                          <span className="w-4 shrink-0">
                            {isSelected && (
                              <CheckIcon className="w-4 h-4" />
                            )}
                          </span>
                          <span className="truncate">{d.label || `Microphone ${d.deviceId.substring(0, 5)}...`}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </Transition>
            </div>

            {/* Camera pill */}
            {videoEnabled && !requireVideo && (
              <button
                onClick={() => {
                  void session.toggleVideoPause();
                  trackInCallControl('live_interview.control_camera_toggled', {
                    state: session.isVideoPaused ? 'on' : 'off',
                  });
                }}
                disabled={!isBackgroundResearchComplete}
                className={`${pillBase} ${pillStd} ${!isBackgroundResearchComplete ? pillDisabled : session.isVideoPaused ? pillActive : pillIdle}`}
                title="Toggle camera"
              >
                {session.isVideoPaused ? <VideoOffIcon className="w-[22px] h-[22px]" /> : <VideoIcon className="w-[22px] h-[22px]" />}
              </button>
            )}

            {/* Screen share pill */}
            {screenShareAvailable && (
              <button
                onClick={() => {
                  trackInCallControl('live_interview.control_screen_share_clicked', {
                    state: screenShare.isSharing ? 'stop' : 'start',
                  });
                  if (screenShare.isSharing) {
                    screenShare.stop('user_stopped');
                    return;
                  }
                  void screenShare.startManual();
                }}
                disabled={!isBackgroundResearchComplete}
                className={`${pillBase} ${pillStd} ${!isBackgroundResearchComplete ? pillDisabled : screenShare.isSharing ? pillActive : pillIdle}`}
                title={screenShare.isSharing ? 'Stop sharing your screen' : 'Share your screen'}
              >
                <ScreenShareIcon className="w-[22px] h-[22px]" />
              </button>
            )}

            {/* Captions (CC) pill */}
            <button
              onClick={() => {
                const nextShowCaptions = !showCaptions;
                setShowCaptions(nextShowCaptions);
                trackInCallControl('live_interview.control_captions_toggled', {
                  state: nextShowCaptions ? 'on' : 'off',
                });
              }}
              className={`${pillBase} ${pillStd} ${showCaptions ? 'bg-surface-raised border-border text-foreground' : pillIdle}`}
              title={showCaptions ? 'Hide captions' : 'Show captions'}
              aria-pressed={showCaptions}
            >
              <CcIcon className="w-[22px] h-[22px]" />
            </button>

            {/* Nudge (raised hand) pill - desktop only; mobile users access it via the three-dot menu */}
            <button
              onClick={session.handleNudgeAi}
              disabled={session.isNudging || !isBackgroundResearchComplete}
              className={`hidden md:flex ${pillBase} ${pillStd} ${(session.isNudging || !isBackgroundResearchComplete) ? pillDisabled : pillIdle}`}
              title="Nudge hollie to move on"
              aria-label="Nudge hollie to move on"
              aria-busy={session.isNudging}
            >
              <ButtonContent loading={session.isNudging}>
                <svg className="w-[22px] h-[22px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 11V5.5a1.5 1.5 0 013 0V11m0-1V4.5a1.5 1.5 0 013 0V11m0 0V6.5a1.5 1.5 0 013 0V14a6 6 0 01-6 6h-1a6 6 0 01-5.7-4.1l-1.1-3.4a1.5 1.5 0 012.6-1.4L9 13" />
                </svg>
              </ButtonContent>
            </button>

            {/* Three-dot menu - slimmer pill */}
            <div className="relative" ref={session.reportMenuRef}>
              <button
                ref={reportMenuTriggerRef}
                onClick={() => session.setShowReportMenu(!session.showReportMenu)}
                className={`${pillBase} ${pillSlim} ${pillIdle}`}
                title="More options"
                aria-haspopup="menu"
                aria-expanded={session.showReportMenu}
              >
                <IconDotsVertical className="w-[22px] h-[22px]" stroke={2} />
              </button>

              <Transition show={session.showReportMenu} as={React.Fragment}>
                <div aria-hidden={!session.showReportMenu} inert={!session.showReportMenu} className="absolute bottom-full right-0 mb-3 w-64 origin-bottom-right bg-surface-raised border border-border rounded-xl overflow-hidden z-50 transition duration-200 data-closed:opacity-0 data-closed:translate-y-1 data-closed:scale-[0.98] data-leave:duration-150 motion-reduce:transition-none">
                  {/* Nudge - mobile only, since on desktop it's its own top-level pill */}
                  <button
                    onClick={() => { session.setShowReportMenu(false); session.handleNudgeAi(); }}
                    aria-busy={session.isNudging}
                    disabled={session.isNudging || !isBackgroundResearchComplete}
                    className="md:hidden w-full text-left px-4 py-3 hover:bg-surface-hover text-foreground font-medium text-sm flex items-center gap-3 border-b border-border/60 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ButtonContent loading={session.isNudging}>
                      <svg className="w-5 h-5 text-accent-soft shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 11V5.5a1.5 1.5 0 013 0V11m0-1V4.5a1.5 1.5 0 013 0V11m0 0V6.5a1.5 1.5 0 013 0V14a6 6 0 01-6 6h-1a6 6 0 01-5.7-4.1l-1.1-3.4a1.5 1.5 0 012.6-1.4L9 13" />
                      </svg>
                      Nudge hollie
                    </ButtonContent>
                  </button>

                  <button
                    onClick={() => {
                      session.setShowReportMenu(false);
                      captureEvent('live_interview.control_report_issue_opened', {
                        ...buildLiveInterviewBaseProps(roomId, 'in_call', interviewDataId),
                      });
                      session.setShowReportModal(true);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-surface-hover text-foreground font-medium text-sm flex items-center gap-3"
                  >
                    <WarningIcon className="w-5 h-5 text-warning shrink-0" />
                    Report a problem
                  </button>
                </div>
              </Transition>
            </div>

            {/* End call - red pill with phone-down icon */}
            <button
              onClick={session.requestEndInterview}
              className="flex items-center gap-2 h-[52px] px-5 md:px-6 bg-danger text-danger-foreground font-semibold rounded-full hover:bg-danger transition-all"
              title="End call"
            >
              <EndCallIcon className="w-[22px] h-[22px]" />
              <span className="hidden md:inline text-[15px]">End</span>
            </button>
          </div>

          {/* RIGHT: Side-panel actions stay together in the bottom-right corner. */}
          <div data-testid="call-side-actions" className="flex w-full md:w-auto md:flex-1 items-center justify-end gap-1">

            <button
              onClick={() => {
                const nextOpen = !showInfo;
                captureEvent('live_interview.control_info_panel_toggled', {
                  ...buildLiveInterviewBaseProps(roomId, 'in_call', interviewDataId),
                  state: nextOpen ? 'open' : 'closed',
                });
                setShowInfo(nextOpen);
              }}
              className={`p-3 rounded-full transition-colors ${showInfo ? 'bg-surface-hover text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-surface-hover'}`}
              title="Meeting info"
              aria-label="Meeting info"
              aria-expanded={showInfo}
            >
              <InfoIcon className="w-[22px] h-[22px]" />
            </button>

            <button
              disabled={!conversationPanel}
              className={`p-3 rounded-full transition-colors ${conversationPanel ? 'cursor-pointer hover:bg-surface-hover text-foreground' : 'text-muted-foreground cursor-not-allowed'}`}
              title={conversationPanel ? 'Conversation' : 'Chat coming soon'}
              aria-label={conversationPanel ? 'Conversation' : 'Chat coming soon'}
              aria-expanded={conversationPanelOpen}
              onClick={() => { onInfoPanelChange?.(false); onConversationPanelChange?.(!conversationPanelOpen); }}
            >
              <MessageIcon className="w-[22px] h-[22px]" />
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default CallScreen;
